/**
 * EVENTSFLO-WEB-PUSH-NOTIFICATIONS-PHASE-1-V1
 *
 * Read-only Phase 1 Web Push diagnostic + opt-in/out card.
 *
 * STRICT scope:
 *  - Capability detection (Service Worker, PushManager, Notification).
 *  - Backend VAPID status (configured / not configured).
 *  - Subscribe / Unsubscribe for the CURRENT user only.
 *  - DRY-RUN test-send gated on the exact confirm phrase.
 *  - Tenant admin diagnostic counts + safe row list (no secrets).
 *
 * EXPLICITLY DOES NOT:
 *  - Schedule any notifications
 *  - Notify other users (crew / clients / admins)
 *  - Send any real push (no pywebpush installed in Phase 1)
 *  - Expose endpoint / p256dh / auth / VAPID private key
 */
import React, { useEffect, useState } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';
import api from '../../../lib/api';

const PHRASE = 'SEND TEST PUSH NOTIFICATION';

const fmtTs = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

// Base64-URL → Uint8Array, required by PushManager.subscribe.
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const detectCapabilities = () => {
  const swSupported =
    typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
  const pushSupported =
    typeof window !== 'undefined' && 'PushManager' in window;
  const notifSupported =
    typeof window !== 'undefined' && 'Notification' in window;
  const permission = notifSupported
    ? (window.Notification && window.Notification.permission) || 'default'
    : 'unsupported';
  return { swSupported, pushSupported, notifSupported, permission };
};

export default function PushNotificationsSettingsCard() {
  const [caps, setCaps] = useState(detectCapabilities());
  const [status, setStatus] = useState(null);
  const [diagnostic, setDiagnostic] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phraseInput, setPhraseInput] = useState('');
  const [lastTestResult, setLastTestResult] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        api.get('/push/status'),
        api.get('/admin/push/subscriptions/status', {
          params: { limit: 50 },
        }),
      ]);
      setStatus(s.data || null);
      setDiagnostic(d.data || null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message
        || 'Failed to load push status.';
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCaps(detectCapabilities());
    refresh();
  }, []);

  const canSubscribe = (
    caps.swSupported && caps.pushSupported
    && caps.permission !== 'denied'
    && status && status.vapid_configured
  );

  const subscribe = async () => {
    if (!canSubscribe) return;
    setBusy(true);
    try {
      if (caps.permission === 'default') {
        const perm = await window.Notification.requestPermission();
        setCaps((c) => ({ ...c, permission: perm }));
        if (perm !== 'granted') {
          toast.error('Notification permission was not granted.');
          return;
        }
      }
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      let sub = existing;
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(
            status.vapid_public_key,
          ),
        });
      }
      const json = sub.toJSON();
      await api.post('/push/subscribe', {
        endpoint: json.endpoint,
        keys: json.keys,
        portal: 'admin',
      });
      toast.success('Subscribed to push notifications.');
      await refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message
        || 'Failed to subscribe.';
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      let endpoint = null;
      if (caps.swSupported && caps.pushSupported) {
        try {
          const reg = await navigator.serviceWorker.ready;
          const sub = await reg.pushManager.getSubscription();
          if (sub) {
            endpoint = sub.endpoint;
            await sub.unsubscribe();
          }
        } catch { /* ignore — backend will revoke by user_id */ }
      }
      await api.post('/push/unsubscribe', endpoint ? { endpoint } : {});
      toast.success('Unsubscribed from push notifications.');
      await refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message
        || 'Failed to unsubscribe.';
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    setLastTestResult(null);
    try {
      const r = await api.post('/push/test-send', {
        confirm_phrase: phraseInput,
      });
      setLastTestResult(r.data || null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message
        || 'Test push failed.';
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      id="push-notifications-settings-panel"
      data-testid="push-notifications-settings-card"
      className="border-zinc-800"
    >
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle data-testid="push-notifications-settings-title">
            Push notifications
          </CardTitle>
          <p
            className="text-xs text-muted-foreground mt-1"
            data-testid="push-notifications-settings-readonly-note"
          >
            Phase 1 foundation only — capture subscriptions and read-only
            diagnostics. No live operational notifications are sent yet.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={refresh}
          disabled={loading}
          data-testid="push-notifications-refresh-btn"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </CardHeader>

      <CardContent className="space-y-4 text-xs">
        {/* Browser capability block */}
        <div
          className="grid grid-cols-2 gap-2"
          data-testid="push-notifications-capability-block"
        >
          <div>Service worker</div>
          <div data-testid="push-cap-sw">
            {caps.swSupported ? (
              <Badge variant="outline">supported</Badge>
            ) : (
              <Badge variant="destructive">unsupported</Badge>
            )}
          </div>
          <div>PushManager</div>
          <div data-testid="push-cap-pushmanager">
            {caps.pushSupported ? (
              <Badge variant="outline">supported</Badge>
            ) : (
              <Badge variant="destructive">unsupported</Badge>
            )}
          </div>
          <div>Notification permission</div>
          <div data-testid="push-cap-permission">
            <Badge
              variant={
                caps.permission === 'granted' ? 'outline'
                  : caps.permission === 'denied' ? 'destructive'
                    : 'secondary'
              }
            >
              {caps.permission}
            </Badge>
          </div>
        </div>

        {/* Backend status block */}
        <div
          className="grid grid-cols-2 gap-2"
          data-testid="push-notifications-backend-status"
        >
          <div>VAPID configured</div>
          <div data-testid="push-status-vapid">
            {status && status.vapid_configured ? (
              <Badge variant="outline">configured</Badge>
            ) : (
              <Badge variant="destructive">not configured</Badge>
            )}
          </div>
          <div>Push sender installed</div>
          <div data-testid="push-status-sender">
            <Badge variant="secondary">not installed (Phase 1)</Badge>
          </div>
          <div>Active subscriptions (you)</div>
          <div data-testid="push-status-my-active">
            {status?.current_user_active_subscription_count ?? 0}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={subscribe}
            disabled={!canSubscribe || busy}
            data-testid="push-subscribe-btn"
          >
            Subscribe
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={unsubscribe}
            disabled={busy}
            data-testid="push-unsubscribe-btn"
          >
            Unsubscribe
          </Button>
        </div>

        {/* Dry-run test-send */}
        <div
          className="border border-zinc-800 rounded p-3 space-y-2"
          data-testid="push-test-send-block"
        >
          <div className="font-semibold">Send test push (dry-run only)</div>
          <p className="text-muted-foreground">
            Type the exact phrase to enable. Phase 1 never sends an actual
            push — this verifies plumbing only.
          </p>
          <Input
            value={phraseInput}
            onChange={(e) => setPhraseInput(e.target.value)}
            placeholder={PHRASE}
            className="h-8 text-xs font-mono"
            data-testid="push-test-send-phrase-input"
          />
          <Button
            size="sm"
            onClick={sendTest}
            disabled={busy || phraseInput !== PHRASE}
            data-testid="push-test-send-btn"
          >
            Send test push (dry-run)
          </Button>
          {lastTestResult && (
            <pre
              className="text-[10px] bg-zinc-900/50 p-2 rounded overflow-x-auto"
              data-testid="push-test-send-result"
            >
              {JSON.stringify(lastTestResult, null, 2)}
            </pre>
          )}
        </div>

        {/* Admin diagnostic counts + list */}
        {diagnostic && (
          <div
            className="border-t border-zinc-800 pt-3 space-y-2"
            data-testid="push-diagnostic-block"
          >
            <div className="font-semibold">Tenant diagnostic</div>
            <div className="grid grid-cols-2 gap-2">
              <div>Total</div>
              <div data-testid="push-diagnostic-total">
                {diagnostic.total_count}
              </div>
              <div>Active</div>
              <div data-testid="push-diagnostic-active">
                {diagnostic.active_count}
              </div>
              <div>By portal (active)</div>
              <div
                className="font-mono"
                data-testid="push-diagnostic-by-portal"
              >
                {JSON.stringify(diagnostic.by_portal_active || {})}
              </div>
            </div>
            {Array.isArray(diagnostic.rows)
              && diagnostic.rows.length > 0 && (
              <div className="overflow-x-auto">
                <table
                  className="w-full"
                  data-testid="push-diagnostic-table"
                >
                  <thead className="text-muted-foreground">
                    <tr className="text-left">
                      <th className="py-1">When</th>
                      <th>User</th>
                      <th>Portal</th>
                      <th>UA hash</th>
                      <th>Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diagnostic.rows.slice(0, 20).map((r, i) => (
                      <tr
                        key={r.id || i}
                        className="border-t border-zinc-800"
                        data-testid={`push-diagnostic-row-${i}`}
                      >
                        <td className="py-1">{fmtTs(r.updated_at)}</td>
                        <td>{r.user_email || r.user_id || '—'}</td>
                        <td>{r.portal || '—'}</td>
                        <td className="font-mono text-[10px]">
                          {r.user_agent_hash
                            ? `${r.user_agent_hash.slice(0, 12)}…`
                            : '—'}
                        </td>
                        <td>
                          {r.is_active ? (
                            <Badge variant="outline">yes</Badge>
                          ) : (
                            <Badge variant="secondary">no</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
